import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  forwardRef,
  Ref,
  useImperativeHandle,
} from 'react';
import _ from 'lodash';
import classnames from 'classnames/bind';
import {
  IMake,
  IModel,
  ISearchVehicle,
  IVehicleCategory,
  IVehicleShort,
  Replacement,
} from '../../../../interface';
import VehicleList from '../VehicleList';
import FilterDropdown from '../../FilterDropdown';
import FilterResult from '../../FilterResult';
import ToggleFilter from '../../../ToggleFilter';
import style from './index.module.scss';
import instance from '../../../../services/httpRequest';
import analyseData from '../../../../utils/analyseSearchData';
import Loading from '../../../Loading';
import localesReplacement from '../../../../utils/localesReplacement';
import ClickOut from 'react-click-out';
import {getTypeFromId} from '../../../../utils/matchVehicleCategories';
import {Dispatch} from 'redux';
import actions from '../../../../redux/actions';
import {connect} from 'react-redux';
import Filter from '../../../../utils/filter';
import {useSearchParams, useNavigate} from 'react-router-dom';
import {useAppName} from '../../../../hooks/use-app-names';
import {useAnalytics} from '../../../../hooks/use-analytics';

import {
  APPNAME,
  DURATION,
  FASTSCAN,
  ANALYTICS_EVENT,
  ANALYTICS_PAGE_TYPE,
  ANALYTICS_SEARCH_TYPE,
  ANALYTICS_USER_TYPE,
  BACK_BUTTON_ENABLED_COUNTRIES,
  YEAR_API_CALL_ENABLED_COUNTRIES,
  YEAR_MANDATORY_COUNTRIES
} from "../../../../constants";

import {useMediaQuery} from 'react-responsive';
import backIcon from "../../../../images/icon-back-primary.svg";

export interface VehicleSearchRef {
  clearValue: () => void;
}
interface IProps {
  country?: string;
  replacements: Replacement;
  onVehicleSelect?: (
    value: string | number,
    model: string,
    vehicleType?: string,
  ) => void;
  categoriesType: string;
  selectedWorkshop: boolean;
  restore?: boolean;
  preserved?: boolean;
  setResults: (v: ISearchVehicle[] | null) => void;
  setLoading: (v: boolean) => void;
  setLicenseKeys: (v: string) => void;
  searchHandler: () => void;
  quickSearch: any;
}

const cx = classnames.bind(style);
const classDropdownItem = 'dropdown-item';

const VehicleSearch = forwardRef(function VehicleSearch(
  props: IProps,
  ref: Ref<VehicleSearchRef>,
) {
  const {
    replacements,
    onVehicleSelect,
    categoriesType,
    selectedWorkshop,
    restore,
    preserved,
    setResults,
    setLicenseKeys,
    setLoading,
    searchHandler,
    quickSearch,
    country
  } = props;
  const navigate = useNavigate();
  const isFastScan = useAppName(FASTSCAN);
  const sendAnalytics = useAnalytics();
  const isDesktopOrLaptop = useMediaQuery({minWidth: 1024});
  const [searchParams, setSearchParams] = useSearchParams();
  const [vehicleTypes, setVehicleTypes] = useState<IVehicleCategory[]>();
  const [typeID, setTypeID] = useState<number | string>();
  const [makes, setMakes] = useState<IMake[]>([]);
  const [make, setMake] = useState<string>('');
  const [models, setModels] = useState<IModel[]>([]);
  const [model, setModel] = useState<string>('');
  const [year, setYear] = useState<string>('');
  const [years, setYears] = useState<string[]>([]);
  const [vehicles, setVehicles] = useState<IVehicleShort[]>([]);
  const [filterVisible, setFilterVisible] = useState<boolean>(true);
  const [isMount, setIsMount] = useState<boolean>(false);
  const type = _.chain(vehicleTypes).find(['id', typeID]).get('name').value();
  const controller = new AbortController();

  const modelMapper = (m: any) => {
      if (m.yearFrom && m.yearTo) {
        return m['name'] +
          ` (${m['yearFrom']} - ${m['yearTo'] ?? ''})`;
      }
                          else if (m.yearFrom) {
                            return m['name'] +
                              ` (${m['yearFrom']})`;
                          }
                          else if (m.yearTo) {
                            return m['name'] +
                              ` (- ${m['yearTo']})`;
                          }
                          else {
                            return m.name;
                          }
  };

  const memoDate = useCallback(() => {
    setIsMount(true);
    selectedWorkshop
      ? _.assign(instance.defaults.headers, {isWorkshopOwner: selectedWorkshop})
      : _.unset(instance.defaults.headers, 'isWorkshopOwner');
    instance
      .get('/categories', {signal: controller.signal})
      .then((d) => {
        setVehicleTypes(d.data);
        const searchTypeId = sessionStorage.getItem('searchTypeId');
        const searchParamTypeId = searchParams.get('typeId');
        let typeId = d.data.at(0).id;

        if (searchParamTypeId) {
          if (d.data.find((item: any) => item.id === parseInt(searchParamTypeId))) {
            typeId = parseInt(searchParamTypeId);
          }
        }
        else if (d.data?.find((item: any) => searchTypeId === item.id)) {
          typeId = searchTypeId;
        }
        setTypeID(typeId);
      })
      .then(() => setIsMount(false))
      .catch(() => {
        sendAnalytics({
          event: ANALYTICS_EVENT.VEHICLE_SEARCH_UNAVAILABLE,
          page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
          search: ANALYTICS_SEARCH_TYPE.VEHICLE_SEARCH,
          user: selectedWorkshop ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
          country: country
        });
      });
  }, [selectedWorkshop, country]);

  useEffect(() => {
    return () => {
      controller.abort();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;
    setLicenseKeys('');
    typeID &&
      instance
        .get(`/makes?categoryId=${typeID}`, {signal: controller.signal})
        .then((d) => {
          isMounted && setMakes(d.data);
          isMounted && setIsMount(false);
        });
    return () => {
      isMounted = false;
    };
  }, [country, typeID]);

  useEffect(() => {
    make &&
      instance
        .get(
          `/models?makeId=${_.chain(_.map(makes))
            .find(['name', make])
            .get('id')}`,
          {signal: controller.signal},
        )
        .then((d) => setModels(d.data));
  }, [make]);

  useEffect(() => {
    const d = models
      .map((m) => {
        const modal = m['model'];
        let name = modal;
        if (m.yearFrom && m.yearTo) {
          name = modal +
            ` (${m['yearFrom']} - ${m['yearTo'] ?? ''})`;
        }
        else if (m.yearFrom) {
          name = modal +
            ` (${m['yearFrom']})`;
        }
        else if (m.yearTo) {
          name = modal +
            ` (- ${m['yearTo']})`;
        }
        return {
          ...m,
          model: name,
        };
      }).find((m) => m.model === model);

    if (!d) {
      return;
    }

    const url =
          year && country && YEAR_API_CALL_ENABLED_COUNTRIES.includes(country)
          ? `/vehicleSearch?modelId=${d.id}&year=${year}`
          : `/vehicleSearch?modelId=${d.id}`;

    model && d &&
      instance
        .get(
          url,
          {signal: controller.signal},
        )
        .then((d) => {
          let yrs = analyseData.year(d.data);
          if (yrs.length  ===  0) {
           yrs = analyseData.year(vehicles);
          }
          else {
            setYears(yrs);
          }
          yrs.sort((a:any,b:any) => b-a);
            setYears(yrs);
          setVehicles(d.data);
        });
  }, [model, country && YEAR_API_CALL_ENABLED_COUNTRIES.includes(country) ? year : null]);

  const changeVehicleType = (id: number) => {
    setTypeID(id);
    setMakes([]);
    setMake('');
    setModels([]);
    setModel('');
    setVehicles([]);
    setYear('');
    restore && sessionStorage.setItem('searchTypeId', _.toString(id));
    restore && sessionStorage.removeItem('searchMake');
    restore && sessionStorage.removeItem('searchModel');
    restore && sessionStorage.removeItem('searchYear');
    reset();
  };

  const changeMake = (make: string) => {
    setMake(make);
    setModels([]);
    setModel('');
    setVehicles([]);
    setYear('');
    restore && sessionStorage.setItem('searchMake', make);
    restore && sessionStorage.removeItem('searchModel');
    restore && sessionStorage.removeItem('searchYear');
    reset();
  };

  const changeModel = (model: string) => {
    setIsSubmitted(false);
    const vehicletype = vehicleTypes?.find((d) => d.id === typeID);
    setModel(model);
    setYear('');
    !model && setVehicles([]);
    restore && sessionStorage.setItem('searchModel', model);
    restore && sessionStorage.removeItem('searchYear');
    reset();
    sendAnalytics({
      event: ANALYTICS_EVENT.VEHICLE_SEARCH,
      page: ANALYTICS_PAGE_TYPE.PRODUCT_FINDER,
      search: ANALYTICS_SEARCH_TYPE.VEHICLE_SEARCH,
      user: selectedWorkshop ? ANALYTICS_USER_TYPE.WORKSHOP_OWNER : ANALYTICS_USER_TYPE.VEHICLE_OWNER,
      category: vehicletype?.name,
      make: make,
      model: model,
      country: country
    });
  };

  const preservedRef = useRef<boolean>();
  useEffect(() => {
    memoDate();
    if (!preserved || _.isEqual(preservedRef.current, preserved)) {
      setMakes([]);
      setMake('');
      setModels([]);
      setModel('');
      setVehicles([]);
      setYear('');
      restore && sessionStorage.removeItem('searchTypeId');
      restore && sessionStorage.removeItem('searchMake');
      restore && sessionStorage.removeItem('searchModel');
      restore && sessionStorage.removeItem('searchYear');
    }
    preservedRef.current = preserved;
  }, [selectedWorkshop, preserved, country]);

  useEffect(() => {
    if (preserved) {
      sessionStorage.getItem('searchTypeId') &&
        setTypeID(
          _.toNumber(sessionStorage.getItem('searchTypeId')) ?? undefined,
        );
    }
  }, [preserved]);
  useEffect(() => {
    if (preserved && !_.isEmpty(makes)) {
      sessionStorage.getItem('searchMake') &&
        setMake(sessionStorage.getItem('searchMake') ?? '');
    }
  }, [preserved, makes]);
  useEffect(() => {
    if (preserved && !_.isEmpty(models)) {
      sessionStorage.getItem('searchModel') &&
        setModel(sessionStorage.getItem('searchModel') ?? '');
    }
  }, [preserved, models]);
  useEffect(() => {
    if (preserved && !_.isEmpty(vehicles)) {
      sessionStorage.getItem('searchYear') &&
        setYear(sessionStorage.getItem('searchYear') ?? '');
    }
  }, [preserved, vehicles]);

  const [typeVisible, setTypeVisible] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const searchResult = _.map(vehicles, (d) => {
    return {
      ...d,
      vehicleType: type,
      make: make,
      model: model,
    }
  });

  const timeoutRef = useRef<NodeJS.Timeout>();
  const resultRef = useRef<ISearchVehicle[]>();
  useEffect(() => {
    if (isSubmitted) {
      clearTimeout(timeoutRef.current);
      if (!vehicles || _.isEmpty(vehicles)) {
        setResults(null);
        if (make && model) {
          setLicenseKeys(`${localesReplacement(replacements, 'No results found for')} ${make} ${model} ${year}`);
        }
        else {
          setLicenseKeys('');
        }        
      } else {
        if (
          !_.isEmpty(
            new Filter(searchResult)
              .vehicleType(type)
              .manufacturer(make)
              .model(model)
              .year(year)
              .value(),
          )
        ) {
          setResults(
            new Filter(searchResult)
              .vehicleType(type)
              .manufacturer(make)
              .model(model)
              .year(year)
              .value(),
          );
          resultRef.current = new Filter(searchResult)
            .vehicleType(type)
            .manufacturer(make)
            .model(model)
            .year(year)
            .value();
          setLoading(true);
          timeoutRef.current = setTimeout(() => {
            setLoading(false);
          }, DURATION);
        }
      }
    }
  }, [vehicles, model, year, isSubmitted]);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
      setLoading(false);
    };
  }, []);

  useImperativeHandle(
    ref,
    () => {
      return {
        clearValue() {
          if (make) {
            setMakes([]);
            setMake('');
            setModels([]);
            setModel('');
            setVehicles([]);
            setYear('');
            instance
              .get(`/makes?categoryId=${typeID}`, {signal: controller.signal})
              .then((d) => {
                setMakes(d.data);
                setModels([]);
                setModel('');
                setVehicles([]);
                setYear('');
              });
          }
        },
      };
    },
    [vehicleTypes, typeID, make],
  );

  const reset = () => {
    searchHandler();
  };

  const searchDisabled = () => {
    if (YEAR_MANDATORY_COUNTRIES.includes(country ?? 'GBR')) {
      return !(model && year);
    }
    return !model;
  }

  return (
    <>
      {!isMount ? (
        <>
          {!!vehicleTypes && (
            <div className={cx('vehicle-search')}>
              <div className={cx('dropdown-wrapper')}>
                <ClickOut
                  className='click-out'
                  onClickOut={() => setTypeVisible(false)}
                >
                  <div
                    className={cx(
                      'dropdown',
                      {on: typeVisible},
                      _.kebabCase(
                        getTypeFromId(+(typeID ?? 0), categoriesType),
                      ),
                    )}
                    onClick={() => setTypeVisible(!typeVisible)}
                  >
                    {!typeID ? (
                      localesReplacement(
                        replacements,
                        'Select your vehicle or equipment type',
                      )
                    ) : (
                      <>
                        <i></i>
                        <span className='overflowText'>
                          {_.chain(vehicleTypes)
                            .find(['id', typeID])
                            .get('name')
                            .value()}
                        </span>
                      </>
                    )}
                  </div>
                </ClickOut>
                <div className={cx('mask', {on: typeVisible})}></div>
                <div className={cx('flyout', {on: typeVisible})}>
                  <VehicleList
                    dataList={vehicleTypes}
                    selected={typeID ?? 0}
                    changeVehicleTypeHandler={changeVehicleType}
                    categoriesType={categoriesType}
                  />
                </div>
              </div>
              <div className={cx('dropdown-list')}>
                <div className={cx(classDropdownItem)}>
                  <FilterDropdown
                    dataList={makes}
                    filter={make}
                    placeholder={localesReplacement(replacements, 'Type or select brand')}
                    changeHandler={changeMake}
                    type={localesReplacement(replacements, 'Make')}
                    replacements={replacements}
                  />
                </div>
                <div className={cx(classDropdownItem)}>
                  <FilterDropdown
                    dataList={_.chain(models)
                      .map((d) => {
                        return {
                          id: d['id'],
                          name: d['model'],
                          yearFrom: d['yearFrom'],
                          yearTo: d['yearTo'],
                        }
                      })
                      .map(modelMapper)
                      .value()}
                    filter={model}
                    placeholder={localesReplacement(replacements, 'Type or select model')}
                    changeHandler={changeModel}
                    type={localesReplacement(replacements, 'Model')}
                    replacements={replacements}
                  />
                </div>
                <div hidden>
                  <ToggleFilter
                    toggleHandler={setFilterVisible}
                    panelVisible={filterVisible}
                  />
                </div>
                <div
                  className={`yearDropdown ${cx(
                    classDropdownItem,
                    'yearDropdown',
                    {
                      hidden: !filterVisible,
                    },
                  )}`}
                >
                  <FilterDropdown
                    dataList={years}
                    filter={year}
                    placeholder={localesReplacement(replacements, 'Select year')}
                    changeHandler={(v) => {
                      setIsSubmitted(false);
                      setYear(v);
                      reset();
                      sessionStorage.setItem('searchYear', v);
                    }}
                    type={localesReplacement(replacements, 'Year')}
                    replacements={replacements}
                  />
                </div>
              </div>
              <button
                className={cx('search-btn')}
                disabled={searchDisabled()}
                onClick={() => {
                  if(!isSubmitted) {
                    setIsSubmitted(false);
                    setTimeout(() => {
                      setIsSubmitted(true);
                    }, 0);
                    searchHandler();
                    if (isDesktopOrLaptop) {
                      searchParams.delete('id');
                      searchParams.set('page', 'landing');
                      isFastScan && searchParams.set(APPNAME, FASTSCAN);
                      setSearchParams(searchParams);
                    }
                  }
                }}
              >
                {localesReplacement(replacements, 'Search')}
              </button>
            </div>
          )}
          {
            searchParams.get('page') === 'recommendedProducts' && searchResult && BACK_BUTTON_ENABLED_COUNTRIES.includes(country || 'USA') && (
              <button
                className={cx('back-btn')}
                onClick={() => {
                  if (quickSearch.current.value.trim()) {
                    const val = quickSearch.current.value.trim();
                    quickSearch.current.clearValue();
                    setTimeout(() => {
                      quickSearch.current.set(val + ' ');
                    }, 0);
                    return;
                  }
                  setIsSubmitted(false);
                  navigate(-1);
                  setTimeout(() => {
                    setIsSubmitted(true);
                  }, 0);
                }}>
                <img src={backIcon} />
                <span>{localesReplacement(replacements, "Back")}</span>
              </button>
            )
          }
          {isSubmitted && (
            <FilterResult
              dataList={searchResult}
              vehicleType={type}
              make={make}
              model={model}
              year={year}
              onSelect={onVehicleSelect}
              replacements={replacements}
            />
          )}
        </>
      ) : (
        <Loading />
      )}
    </>
  );
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
  setResults: (vehicle: ISearchVehicle[] | null) =>
    dispatch(actions.setResults(vehicle)),
  setLoading: (v: boolean) => dispatch(actions.setLoading(v)),
  setLicenseKeys: (v: string) => dispatch(actions.setLicenseKeys(v)),
});

export default connect(null, mapDispatchToProps, null, {forwardRef: true})(
  VehicleSearch,
);
